import { useEffect, useRef, useState } from 'react'
import type { RoutePath } from '@/hooks/useHashRoute'
import styles from './AccountMenu.module.css'

export type AccountMenuProps = {
  username: string
  onLogout: () => void
  /** The page the menu is rendered on — listed with the active background and
   * `aria-current="page"` so the menu doubles as a "where am I" indicator. */
  currentPage: RoutePath
  onNavigate: (path: RoutePath) => void
  /** Shows the admin-gated pages (stats + admin panel). UX-only — their
   * endpoints are enforced server-side regardless. */
  isAdmin: boolean
}

/** Chat is deliberately absent — the header back arrow covers returning to it. */
const PAGES: ReadonlyArray<{ path: RoutePath; label: string; adminOnly: boolean }> = [
  { path: 'stats', label: 'Statistikk', adminOnly: true },
  { path: 'admin', label: 'Adminpanel', adminOnly: true },
]

export default function AccountMenu({
  username,
  onLogout,
  currentPage,
  onNavigate,
  isAdmin,
}: AccountMenuProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  // Close on click outside or Escape — only wired while the menu is open.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        className={styles.trigger}
        aria-label="Konto"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <UserIcon />
      </button>
      {open && (
        <div className={styles.menu}>
          <div className={styles.user}>
            <UserIcon />
            <span className={styles.username}>{username}</span>
          </div>
          {PAGES.filter((page) => isAdmin || !page.adminOnly).map((page) => (
            <button
              key={page.path}
              className={`${styles.item} ${page.path === currentPage ? styles.itemActive : ''}`}
              aria-current={page.path === currentPage ? 'page' : undefined}
              onClick={() => {
                setOpen(false)
                onNavigate(page.path)
              }}
            >
              {page.label}
            </button>
          ))}
          <button
            className={styles.logout}
            onClick={() => {
              setOpen(false)
              onLogout()
            }}
          >
            Logg ut
          </button>
        </div>
      )}
    </div>
  )
}

function UserIcon() {
  return (
    <svg viewBox="0 0 16 16" width="18" height="18" fill="none" aria-hidden="true">
      <circle cx="8" cy="5.5" r="2.8" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M2.8 13.6a5.6 5.6 0 0 1 10.4 0"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  )
}
